// Run history table with drill-down to step timeline.
//
//   - List runs (paged), filter by workflow + status
//   - Auto-refresh every 5s while any visible run is QUEUED or RUNNING
//   - Drill-down dialog with step-by-step timeline + screenshots inline
//   - Cancel button on QUEUED/RUNNING runs
//   - Failed runs surface a hint to use the Rerun tab
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Box, Paper, Typography, Stack, Chip, IconButton, Tooltip, Button,
    Table, TableHead, TableBody, TableRow, TableCell, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
    Alert, CircularProgress, Select, FormControl, InputLabel,
    LinearProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import BuildIcon from '@mui/icons-material/Build';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import {
    rpaRunList, rpaRunGet, rpaRunCancel, rpaWorkflowList,
} from './api';
import axios from '../../../utils/axiosInstance';
import {
    KpiCard, KpiGrid, SectionPaper, TableShell, EmptyState, Toolbar,
    StatusChip, RPA_COLORS, formatDateLong, formatBytes,
} from './rpaTheme';

// Tiny helper — same shape as the rpaArtifact* helpers in api.js but
// inlined here so we don't have to hop files for one URL.
const rpaArtifactUrl = (artifactId) =>
    axios.get(`/api/finance/rpa/artifacts/${artifactId}/url`);

const TERMINAL_STATUSES = new Set(['succeeded', 'failed', 'cancelled']);

export default function RunList() {
    const [runs, setRuns] = useState([]);
    const [workflows, setWorkflows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [workflowFilter, setWorkflowFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [detailRunId, setDetailRunId] = useState(null);

    const pollTimerRef = useRef(null);

    const loadWorkflows = useCallback(async () => {
        try {
            const r = await rpaWorkflowList({ per_page: 100 });
            if (r?.data?.success) setWorkflows(r.data.data || []);
        } catch { /* non-blocking */ }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await rpaRunList({
                workflow_id: workflowFilter || undefined,
                status: statusFilter || undefined,
                per_page: 50,
            });
            if (r?.data?.success) setRuns(r.data.data || []);
            else setError(r?.data?.message || 'Failed to load runs');
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [workflowFilter, statusFilter]);

    useEffect(() => { loadWorkflows(); }, [loadWorkflows]);
    useEffect(() => { load(); }, [load]);

    const stats = useMemo(() => {
        const succeeded = runs.filter((r) => r.status === 'succeeded').length;
        // Anything that ever ended in a failure state — current or
        // already-retried — counts as "failed" for the at-a-glance KPI.
        // (Legacy 'error' / 'needs_repair' included so pre-migration rows
        // bucket correctly until they age out.)
        const isFailish = (s) =>
            s === 'failed' || s === 'rerun' || s === 'error' || s === 'needs_repair';
        const failed = runs.filter((r) => isFailish(r.status)).length;
        const inFlight = runs.filter(
            (r) => !TERMINAL_STATUSES.has(r.status) && !isFailish(r.status)
        ).length;
        return { succeeded, failed, inFlight, total: runs.length };
    }, [runs]);

    // Auto-poll while any run is in-flight. Stops when nothing's pending.
    useEffect(() => {
        const hasInFlight = runs.some((r) => !TERMINAL_STATUSES.has(r.status));
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        if (hasInFlight) {
            pollTimerRef.current = setInterval(load, 5000);
        }
        return () => {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        };
    }, [runs, load]);

    const cancel = async (run) => {
        if (!window.confirm(`Cancel run #${run.id}?`)) return;
        try {
            await rpaRunCancel(run.id);
            load();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    const workflowName = (id) => {
        const w = workflows.find((x) => x.id === id);
        return w ? `${w.name} v${w.version}` : `#${id}`;
    };

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <KpiGrid>
                <KpiCard
                    label="Total runs (this view)"
                    value={stats.total}
                    icon={<PlayArrowIcon fontSize="small" />}
                    accent={RPA_COLORS.robot}
                />
                <KpiCard
                    label="Succeeded"
                    value={stats.succeeded}
                    icon={<CheckCircleIcon fontSize="small" />}
                    accent={RPA_COLORS.success}
                />
                <KpiCard
                    label="In flight"
                    value={stats.inFlight}
                    icon={<HourglassBottomIcon fontSize="small" />}
                    accent={RPA_COLORS.info}
                    hint={stats.inFlight > 0 ? 'Auto-refreshing' : undefined}
                />
                <KpiCard
                    label="Failed / needs repair"
                    value={stats.failed}
                    icon={<ErrorOutlineIcon fontSize="small" />}
                    accent={RPA_COLORS.danger}
                />
            </KpiGrid>

            <SectionPaper>
                <Toolbar>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                        <InputLabel>Workflow</InputLabel>
                        <Select label="Workflow" value={workflowFilter}
                                onChange={(e) => setWorkflowFilter(e.target.value)}>
                            <MenuItem value="">All workflows</MenuItem>
                            {workflows.map((w) => (
                                <MenuItem key={w.id} value={w.id}>
                                    {w.name} v{w.version}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="QUEUED">Queued</MenuItem>
                            <MenuItem value="RUNNING">Running</MenuItem>
                            <MenuItem value="SUCCEEDED">Succeeded</MenuItem>
                            <MenuItem value="FAILED">Failed</MenuItem>
                            <MenuItem value="RERUN">Rerun (handled)</MenuItem>
                            <MenuItem value="CANCELLED">Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                    <IconButton onClick={load} disabled={loading} size="small">
                        {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                        Auto-refreshes every 5s while a run is in progress.
                    </Typography>
                </Toolbar>

                <TableShell>
                    <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Run #</TableCell>
                            <TableCell>Merchant</TableCell>
                            <TableCell>Workflow</TableCell>
                            <TableCell>Trigger</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Steps</TableCell>
                            <TableCell>Started</TableCell>
                            <TableCell>Duration</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {runs.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={9} sx={{ p: 0, border: 'none' }}>
                                    <EmptyState
                                        icon={<PlayArrowIcon />}
                                        title="No runs yet"
                                        message="Trigger a workflow from the Workflows tab — runs appear here as they execute, with step-by-step screenshots and timings."
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                        {runs.map((r) => {
                            const summary = r.result_summary || {};
                            const stepText = summary.steps_total != null
                                ? `${summary.steps_succeeded || 0}/${summary.steps_total}`
                                : '—';
                            return (
                                <TableRow key={r.id} hover>
                                    <TableCell>{r.id}</TableCell>
                                    <TableCell>
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                            {r.merchant_name || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {r.workflow_id
                                                ? (r.workflow_name || workflowName(r.workflow_id))
                                                : '(recording)'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={r.trigger_kind} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <StatusChip status={r.status} />
                                            {/* "Cancelling…" hint while the worker
                                                hasn't reached its next step
                                                boundary yet. Without this the
                                                status stays RUNNING after the
                                                cancel click and the user thinks
                                                nothing happened. */}
                                            {r.cancellation_requested
                                              && r.status === 'running' && (
                                                <Tooltip title="Cancellation requested — the bot will stop at the next step boundary.">
                                                    <Chip
                                                        label="Cancelling…"
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                        sx={{ height: 20, fontSize: 10 }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        {stepText}
                                        {summary.steps_healed > 0 && (
                                            <Tooltip title={`${summary.steps_healed} step(s) recovered via Gemini self-heal`}>
                                                <Chip
                                                    label={`+${summary.steps_healed} healed`}
                                                    size="small"
                                                    color="info"
                                                    sx={{ ml: 0.5 }}
                                                />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {formatDate(r.started_at) || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {formatDuration(r.started_at, r.finished_at)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="View details">
                                            <IconButton size="small" onClick={() => setDetailRunId(r.id)}>
                                                <OpenInFullIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {!TERMINAL_STATUSES.has(r.status)
                                          && r.status !== 'failed'
                                          && r.status !== 'rerun'
                                          && r.status !== 'error'
                                          && r.status !== 'needs_repair' && (
                                            <Tooltip title={r.cancellation_requested
                                                ? 'Cancellation already requested — waiting for the worker to stop at its next step boundary.'
                                                : 'Cancel'}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="warning"
                                                        onClick={() => cancel(r)}
                                                        disabled={!!r.cancellation_requested}
                                                    >
                                                        <CancelIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                        {r.status === 'failed' && (
                                            <Tooltip title="Open the Rerun tab to retry this run">
                                                <span>
                                                    <IconButton size="small" disabled>
                                                        <BuildIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                </TableShell>
            </SectionPaper>

            <RunDetailDialog runId={detailRunId} onClose={() => setDetailRunId(null)} />
        </Box>
    );
}


function RunDetailDialog({ runId, onClose }) {
    const [run, setRun] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (runId == null) return;
        setLoading(true);
        setError(null);
        try {
            const r = await rpaRunGet(runId);
            if (r?.data?.success) setRun(r.data.data);
            else setError(r?.data?.message || 'Failed to load run');
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [runId]);

    useEffect(() => { load(); }, [load]);

    // Auto-refresh while in-flight.
    useEffect(() => {
        if (!run) return;
        if (TERMINAL_STATUSES.has(run.status)) return;
        const t = setInterval(load, 3000);
        return () => clearInterval(t);
    }, [run, load]);

    return (
        <Dialog open={runId != null} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <PlayArrowIcon sx={{ color: RPA_COLORS.robot }} />
                    <Typography variant="h6">Run #{runId}</Typography>
                    {run && <StatusChip status={run.status} />}
                </Stack>
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {(loading || !run) && <LinearProgress sx={{ mb: 2 }} />}
                {run && (
                    <>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                            <Chip label={`Trigger: ${run.trigger_kind}`} size="small" />
                            <Chip label={`Started: ${formatDate(run.started_at) || '—'}`} size="small" />
                            <Chip label={`Duration: ${formatDuration(run.started_at, run.finished_at)}`} size="small" />
                        </Stack>
                        {run.error_message && (
                            <Alert severity="error" sx={{ mb: 2 }}>{run.error_message}</Alert>
                        )}
                        {run.result_summary && (
                            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Summary
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
                                    {Object.entries(run.result_summary).map(([k, v]) => (
                                        <Chip
                                            key={k}
                                            size="small"
                                            label={`${k}: ${v}`}
                                            variant="outlined"
                                        />
                                    ))}
                                </Stack>
                            </Paper>
                        )}

                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Steps ({(run.steps || []).length})
                        </Typography>
                        <Stack spacing={1}>
                            {(run.steps || []).map((s) => {
                                // Selector — primary CSS comes from the
                                // workflow definition merged in by the
                                // backend. selector_used is what the
                                // executor ACTUALLY used (may differ from
                                // primary if a fallback or self-heal hit).
                                const primarySel = (s.selector && s.selector.primary)
                                    || s.selector_used || '';
                                const xpathSel = s.selector && s.selector.xpath;
                                const textSel = s.selector && s.selector.text;
                                // Redact password placeholders so we never
                                // print real secrets even if value resolution
                                // happened server-side. (We capture as
                                // {{__password__}} so this should already
                                // be the placeholder for any password field.)
                                let valDisplay = null;
                                if (s.value !== undefined && s.value !== null && s.value !== '') {
                                    const str = String(s.value);
                                    valDisplay = str.includes('{{__password__}}')
                                        ? '••••••• (password placeholder)'
                                        : (str.length > 200 ? str.slice(0, 200) + '…' : str);
                                }
                                const df = s.date_filter;
                                return (
                                <Paper key={s.id} variant="outlined" sx={{ p: 1.25 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Chip label={`#${s.step_idx}`} size="small" />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {s.action}
                                        </Typography>
                                        <Chip
                                            label={s.selector_strategy || 'primary'}
                                            size="small"
                                            color={s.selector_strategy === 'gemini_vision' ? 'info' : 'default'}
                                            variant="outlined"
                                        />
                                        {s.retry_count > 0 && (
                                            <Chip label={`${s.retry_count} retries`} size="small" />
                                        )}
                                        {df && df.preset && (
                                            <Chip
                                                label={`📅 ${df.preset}`}
                                                size="small"
                                                color="info"
                                                variant="outlined"
                                            />
                                        )}
                                        {s.semantic_label && (
                                            <Chip
                                                label={s.semantic_label}
                                                size="small"
                                                variant="outlined"
                                                sx={{ maxWidth: 240 }}
                                            />
                                        )}
                                        <Box sx={{ flex: 1 }} />
                                        {s.ms_elapsed != null && (
                                            <Typography variant="caption" color="text.secondary">
                                                {s.ms_elapsed} ms
                                            </Typography>
                                        )}
                                    </Stack>
                                    {primarySel && (
                                        <Typography variant="caption" color="text.secondary"
                                                    sx={{ display: 'block', fontFamily: 'monospace',
                                                          mt: 0.5, wordBreak: 'break-all' }}>
                                            <strong>selector:</strong> {primarySel}
                                        </Typography>
                                    )}
                                    {xpathSel && xpathSel !== primarySel && (
                                        <Typography variant="caption" color="text.secondary"
                                                    sx={{ display: 'block', fontFamily: 'monospace',
                                                          wordBreak: 'break-all', opacity: 0.7 }}>
                                            <strong>xpath:</strong> {xpathSel}
                                        </Typography>
                                    )}
                                    {textSel && (
                                        <Typography variant="caption" color="text.secondary"
                                                    sx={{ display: 'block', opacity: 0.7 }}>
                                            <strong>text:</strong> "{textSel}"
                                        </Typography>
                                    )}
                                    {valDisplay !== null && (
                                        <Typography variant="caption" color="text.secondary"
                                                    sx={{ display: 'block', mt: 0.5,
                                                          wordBreak: 'break-all' }}>
                                            <strong>value:</strong> {valDisplay}
                                        </Typography>
                                    )}
                                    {df && df.preset && (
                                        <Typography variant="caption"
                                                    sx={{ display: 'block', mt: 0.5,
                                                          color: RPA_COLORS.info }}>
                                            <strong>date filter:</strong>{' '}
                                            recipe <code>{df.preset}</code>
                                            {df.format ? ` · ${df.format}` : ''}
                                            {df.preset === 'n_days_ago' && df.offset_value
                                                ? `, ${df.offset_value} ${df.offset_unit || 'days'} ago`
                                                : ''}
                                            {df.preset === 'literal' && df.literal
                                                ? ` → "${df.literal}"`
                                                : ''}
                                        </Typography>
                                    )}
                                    {s.error && (
                                        <Alert severity="error" sx={{ mt: 1 }}>{s.error}</Alert>
                                    )}
                                </Paper>
                                );
                            })}
                        </Stack>

                        {run.artifacts && run.artifacts.length > 0 && (
                            <>
                                {/* Surface the run's video first if present —
                                    it's the most useful thing for understanding
                                    what happened. */}
                                <RunVideoPlayer
                                    artifacts={run.artifacts}
                                />

                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                    Artifacts ({run.artifacts.length})
                                </Typography>
                                <Stack spacing={0.5}>
                                    {run.artifacts.map((a) => (
                                        <Paper key={a.id} variant="outlined" sx={{ p: 1 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Chip label={a.kind} size="small"
                                                      color={a.kind === 'video' ? 'info' : 'default'} />
                                                <Typography variant="body2" sx={{ flex: 1 }}>
                                                    {a.filename || a.storage_path}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatBytes(a.size_bytes)}
                                                </Typography>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}


function formatDate(iso) {
    return formatDateLong(iso);
}


// ----- RunVideoPlayer -------------------------------------------------------
// Shows the run's recorded webm inline. We hit /artifacts/<id>/url to get a
// playable URL (signed GCS link, or local /uploads path for legacy rows),
// then render <video controls>. Lazy-fetches the URL on first mount.

function RunVideoPlayer({ artifacts }) {
    const [videoUrl, setVideoUrl] = React.useState(null);
    const [error, setError] = React.useState(null);

    const videoArtifact = useMemo(
        () => (artifacts || []).find((a) => a.kind === 'video'),
        [artifacts],
    );

    useEffect(() => {
        if (!videoArtifact) { setVideoUrl(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const r = await rpaArtifactUrl(videoArtifact.id);
                if (cancelled) return;
                if (r?.data?.success && r.data.data?.url) {
                    setVideoUrl(r.data.data.url);
                } else {
                    setError(r?.data?.message || 'Could not load video URL');
                }
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.message || e.message);
            }
        })();
        return () => { cancelled = true; };
    }, [videoArtifact]);

    if (!videoArtifact) return null;

    return (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Chip label="Recording" size="small" color="info" />
                <Typography variant="caption" color="text.secondary">
                    {videoArtifact.filename} · {formatBytes(videoArtifact.size_bytes)}
                </Typography>
            </Stack>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            {videoUrl ? (
                <video
                    controls
                    preload="metadata"
                    src={videoUrl}
                    style={{
                        width: '100%',
                        maxHeight: 480,
                        background: '#0f172a',
                        borderRadius: 4,
                    }}
                />
            ) : !error ? (
                <LinearProgress />
            ) : null}
        </Paper>
    );
}

function formatDuration(startIso, endIso) {
    if (!startIso) return '—';
    const start = new Date(startIso);
    const end = endIso ? new Date(endIso) : new Date();
    const ms = end - start;
    if (ms < 1000) return `${ms} ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}m ${secs}s`;
}
